// apps/web/src/hooks/use-pendencias-sidebar.ts — Fase 17.8, Plano 13 (D-15; T-17.8-13-03).
// Contagens de pendência por módulo para os badges da sidebar. Lê GET /painel/pendencias (1) ao montar e a cada navegação,
// (2) quando chega um `painel_invalidar` com `badges: true` (debounce de 5 s, pelo canal ao vivo único da aba), (3) a cada ~2 min
// só com a aba visível e (4) ao voltar ao primeiro plano depois de mais de 2 min (sem pulso). O valor anterior fica guardado para
// decidir quem pulsa (`chavesQuePulsam`). Falha mantém o último valor; 403 desliga tudo (as permissões não mudam no meio da sessão);
// a leitura é única em voo (dedupe em `carregarPendencias`), então os badges nunca bloqueiam nem quebram a navegação.
import { useEffect, useRef, useState } from 'react';
import type { PainelPendenciasResponse } from '@pieltec/contracts';
import { useCanalAoVivo } from '../components/shell/ao-vivo-provider';
import { criarDebounce } from '../lib/kanban/sse';
import { DEBOUNCE_REFETCH_MS, RECARGA_VISIVEL_MS } from '../lib/painel/home/ao-vivo';
import { SEM_PENDENCIAS, carregarPendencias, chavesQuePulsam, mesmasPendencias, type ContextoCarga } from '../lib/painel/pendencias';

export interface EstadoPendencias {
  readonly pendencias: PainelPendenciasResponse;
  /** Chaves (`grupo:{id}`, `item:{id}`) cuja última mudança merece o pulso. */
  readonly pulsos: ReadonlySet<string>;
}

const ESTADO_VAZIO: EstadoPendencias = { pendencias: SEM_PENDENCIAS, pulsos: new Set() };

export interface OpcoesPendenciasSidebar {
  /** Há sessão confirmada? Sem ela nada é lido. */
  readonly ativo: boolean;
  /** Identifica a sessão: trocar de usuário descarta o valor anterior. */
  readonly chaveSessao: string | undefined;
  readonly pathname: string;
}

export function usePendenciasSidebar({ ativo, chaveSessao, pathname }: OpcoesPendenciasSidebar): EstadoPendencias {
  const { assinar } = useCanalAoVivo();
  const [estado, setEstado] = useState<EstadoPendencias>(ESTADO_VAZIO);
  const anterior = useRef<PainelPendenciasResponse | null>(null);
  const bloqueado = useRef(false);
  const ultimaLeituraEm = useRef(0);
  const carregarRef = useRef<(contexto: ContextoCarga) => void>(() => undefined);

  // Leitor estável por sessão; guarda o valor anterior e decide os pulsos.
  useEffect(() => {
    if (!ativo) return undefined;
    let vivo = true;
    anterior.current = null;
    bloqueado.current = false;
    ultimaLeituraEm.current = 0;
    setEstado(ESTADO_VAZIO);

    carregarRef.current = (contexto) => {
      if (bloqueado.current) return;
      ultimaLeituraEm.current = Date.now();
      void carregarPendencias().then((resultado) => {
        if (!vivo) return;
        if (resultado.tipo === 'erro') return; // mantém o último valor, sem toast
        if (resultado.tipo === 'sem-acesso') {
          bloqueado.current = true;
          anterior.current = null;
          setEstado(ESTADO_VAZIO);
          return;
        }
        const atual = resultado.pendencias;
        const pulsos = chavesQuePulsam(anterior.current, atual, contexto);
        anterior.current = atual;
        setEstado((velho) => (mesmasPendencias(velho.pendencias, atual) ? velho : { pendencias: atual, pulsos }));
      });
    };

    const visivel = (): boolean => document.visibilityState === 'visible';
    const aoVisibilidade = (): void => {
      if (!visivel()) return;
      if (Date.now() - ultimaLeituraEm.current >= RECARGA_VISIVEL_MS) carregarRef.current('retorno-aba');
    };
    const relogio = setInterval(() => {
      if (visivel()) carregarRef.current('carga');
    }, RECARGA_VISIVEL_MS);
    const debounced = criarDebounce(() => {
      if (vivo) carregarRef.current('carga');
    }, DEBOUNCE_REFETCH_MS);
    const cancelarAssinatura = assinar((evento) => {
      if (evento.tipo === 'painel_invalidar' && evento.badges) debounced();
    });
    document.addEventListener('visibilitychange', aoVisibilidade);

    return () => {
      vivo = false;
      clearInterval(relogio);
      cancelarAssinatura();
      document.removeEventListener('visibilitychange', aoVisibilidade);
      carregarRef.current = () => undefined;
    };
  }, [ativo, chaveSessao, assinar]);

  // Ao montar e a cada navegação: uma leitura (a primeira não pulsa; as seguintes só pulsam se a contagem mudou).
  useEffect(() => {
    if (ativo) carregarRef.current('carga');
  }, [ativo, chaveSessao, pathname]);

  return estado;
}
